import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

function Offers() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const offersRef = collection(db, 'offers');
      const querySnapshot = await getDocs(offersRef);
      const offersData = [];
      querySnapshot.forEach((d) => offersData.push({ id: d.id, ...d.data() }));

      // Sort by priority desc then createdAt desc
      offersData.sort((a, b) => {
        const pa = a.priority || 0;
        const pb = b.priority || 0;
        if (pa !== pb) return pb - pa;
        const ta = a.createdAt ? a.createdAt.seconds || 0 : 0;
        const tb = b.createdAt ? b.createdAt.seconds || 0 : 0;
        return tb - ta;
      });

      setOffers(offersData);
      setError(null);
    } catch (err) {
      console.error('Error fetching offers:', err);
      setError('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, []);

  const handleEdit = (offerId) => navigate(`/offers/${offerId}`);

  const handleDeleteClick = (offer) => {
    setOfferToDelete(offer);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!offerToDelete) return;
    try {
      await deleteDoc(doc(db, 'offers', offerToDelete.id));
      setOffers(offers.filter(o => o.id !== offerToDelete.id));
      setDeleteConfirmOpen(false);
      setOfferToDelete(null);
    } catch (err) {
      console.error('Error deleting offer:', err);
      setError('Failed to delete offer');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return 'N/A';
    try {
      const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch (e) {
      return 'N/A';
    }
  };

  const expiryState = (ts) => {
    if (!ts) return 'none';
    try {
      const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
      const now = new Date();
      if (d <= now) return 'expired';
      const msInDay = 24 * 60 * 60 * 1000;
      if ((d - now) <= (3 * msInDay)) return 'expiring';
      return 'active';
    } catch (e) {
      return 'none';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Offers</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/offers/new')}>
          New Offer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Advertiser</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Published</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {offers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No offers found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              offers.map((offer) => (
                <TableRow key={offer.id} hover>
                  <TableCell>{offer.title || 'Untitled'}</TableCell>
                  <TableCell>{offer.advertiser?.name || 'N/A'}</TableCell>
                  <TableCell>{offer.product?.name || 'N/A'}</TableCell>
                  <TableCell>
                    {offer.discount ? (
                      offer.discount.type === 'percentage' ? `${offer.discount.value}%` : `$${offer.discount.value}`
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {offer.published ? <Chip label="Published" color="success" size="small" /> : <Chip label="Draft" size="small" />}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const state = expiryState(offer.discount?.expiresAt);
                      if (state === 'expired') return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Expired" color="error" size="small" />
                          <Typography variant="body2" color="text.secondary">{formatDate(offer.discount?.expiresAt)}</Typography>
                        </Box>
                      );
                      if (state === 'expiring') return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Expiring" color="warning" size="small" />
                          <Typography variant="body2" color="text.secondary">{formatDate(offer.discount?.expiresAt)}</Typography>
                        </Box>
                      );
                      return formatDate(offer.discount?.expiresAt);
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(offer.id)} color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(offer)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the offer "{offerToDelete?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Offers;
